import React, { useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import type { ClaimEditHistoryEntryDto } from '@common/synapse/introspection-types';

const EVENT_LABELS: Record<string, string> = {
  'claim.corrected': 'Corrected',
  'claim.retracted': 'Retracted',
  'claim.restored': 'Restored',
  'claim.manual_added': 'Manual claim added',
  'claim.confidence_adjusted': 'Confidence adjusted',
};

const FIELD_LABELS: Record<string, string> = {
  predicate: 'Predicate',
  value: 'Value',
  confidence: 'Confidence',
  scope: 'Scope',
  key: 'Key',
  status: 'Status',
  provenance: 'Provenance',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function formatFieldValue(field: string, value: unknown): string {
  if (value == null) return '—';
  if (field === 'confidence' && typeof value === 'number') return value.toFixed(2);
  if (field === 'scope' && Array.isArray(value)) return value.join(', ');
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

function changedFields(
  before: unknown,
  after: unknown,
  fieldsChanged?: unknown
): string[] {
  if (Array.isArray(fieldsChanged) && fieldsChanged.every((f) => typeof f === 'string')) {
    return fieldsChanged;
  }
  if (!isRecord(before) || !isRecord(after)) {
    return isRecord(after) ? Object.keys(after) : [];
  }
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  return Array.from(keys).filter((key) => {
    const b = before[key];
    const a = after[key];
    return JSON.stringify(b) !== JSON.stringify(a);
  });
}

function editorLabel(metadata: Record<string, unknown>): string | null {
  if (typeof metadata.editorEmail === 'string' && metadata.editorEmail) {
    return metadata.editorEmail;
  }
  if (typeof metadata.editorId === 'string' && metadata.editorId) {
    return metadata.editorId.slice(0, 8) + '…';
  }
  return null;
}

function eventSummary(entry: ClaimEditHistoryEntryDto): string | null {
  const meta = entry.metadata ?? {};
  if (typeof meta.note === 'string' && meta.note.trim()) return meta.note;
  if (typeof meta.rationale === 'string' && meta.rationale.trim()) return meta.rationale;
  if (typeof meta.reason === 'string' && meta.reason.trim()) {
    return meta.reason.replace(/_/g, ' ');
  }
  return null;
}

function ChangeRow({ field, before, after }: { field: string; before: unknown; after: unknown }) {
  const label = FIELD_LABELS[field] ?? field;
  const beforeText = formatFieldValue(field, before);
  const afterText = formatFieldValue(field, after);

  return (
    <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ py: 0.25, flexWrap: 'wrap' }} useFlexGap>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 72 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', textDecoration: 'line-through' }}>
        {beforeText}
      </Typography>
      <ArrowForwardIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
      <Typography variant="body2">{afterText}</Typography>
    </Stack>
  );
}

function EditHistoryEntry({ entry }: { entry: ClaimEditHistoryEntryDto }) {
  const [rawExpanded, setRawExpanded] = useState(false);
  const meta = entry.metadata ?? {};
  const editor = editorLabel(meta);
  const summary = eventSummary(entry);
  const fields = changedFields(entry.before, entry.after, meta.fieldsChanged);
  const eventLabel = EVENT_LABELS[entry.eventType] ?? entry.eventType.replace('claim.', '').replace(/_/g, ' ');
  const afterSnapshot = isRecord(entry.after) ? entry.after : null;

  return (
    <Box
      sx={{
        mb: 1.5,
        p: 1.25,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'action.hover',
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
        <Chip size="small" label={eventLabel} variant="outlined" />
        <Typography variant="caption" color="text.secondary">
          {new Date(entry.createdAt).toLocaleString()}
        </Typography>
        {editor && (
          <Typography variant="caption" color="text.secondary">
            · {editor}
          </Typography>
        )}
      </Stack>

      {summary && (
        <Typography variant="body2" sx={{ mb: fields.length > 0 ? 0.75 : 0 }}>
          {summary}
        </Typography>
      )}

      {entry.eventType === 'claim.manual_added' && afterSnapshot && (
        <Stack spacing={0.25}>
          {(['predicate', 'value', 'confidence', 'scope'] as const).map((field) =>
            field in afterSnapshot ? (
              <Stack key={field} direction="row" spacing={0.75} alignItems="baseline">
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 72 }}>
                  {FIELD_LABELS[field] ?? field}
                </Typography>
                <Typography variant="body2">{formatFieldValue(field, afterSnapshot[field])}</Typography>
              </Stack>
            ) : null
          )}
        </Stack>
      )}

      {entry.eventType !== 'claim.manual_added' && fields.length > 0 && (
        <Box sx={{ mt: summary ? 0 : 0 }}>
          {fields.map((field) => (
            <ChangeRow
              key={field}
              field={field}
              before={isRecord(entry.before) ? entry.before[field] : undefined}
              after={isRecord(entry.after) ? entry.after[field] : undefined}
            />
          ))}
        </Box>
      )}

      {entry.eventType === 'claim.confidence_adjusted' && typeof meta.delta === 'number' && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Δ {meta.delta > 0 ? '+' : ''}
          {meta.delta.toFixed(2)}
        </Typography>
      )}

      <Stack direction="row" alignItems="center" sx={{ mt: 0.75 }}>
        <IconButton
          size="small"
          onClick={() => setRawExpanded((v) => !v)}
          aria-label={rawExpanded ? 'Hide raw audit data' : 'Show raw audit data'}
          sx={{ ml: -0.5, p: 0.25 }}
        >
          {rawExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <Typography variant="caption" color="text.secondary" sx={{ cursor: 'pointer' }} onClick={() => setRawExpanded((v) => !v)}>
          Raw audit data
        </Typography>
      </Stack>
      <Collapse in={rawExpanded}>
        <Box
          component="pre"
          sx={{
            fontSize: '0.65rem',
            m: 0,
            mt: 0.5,
            p: 1,
            borderRadius: 1,
            bgcolor: 'background.paper',
            overflow: 'auto',
            maxHeight: 160,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {JSON.stringify({ before: entry.before, after: entry.after, metadata: entry.metadata }, null, 2)}
        </Box>
      </Collapse>
    </Box>
  );
}

export interface ClaimEditHistoryListProps {
  entries: ClaimEditHistoryEntryDto[];
}

const ClaimEditHistoryList = ({ entries }: ClaimEditHistoryListProps) => {
  const sorted = [...entries].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <Box>
      {sorted.map((entry) => (
        <EditHistoryEntry key={entry.id} entry={entry} />
      ))}
    </Box>
  );
};

export default ClaimEditHistoryList;
